<template>
  <loader :is-loading="isLoading" color="primary" :full-page="true">
    <v-flex>
      <v-card flat id="employee-card" class="pa-5">
        <v-card-title id="employee-title" class="form-title">
          <span>{{ $t(`components.employeeForm.${mode}.title`) }}</span>
          <v-spacer></v-spacer>
          <v-icon @click="forceClear()">mdi-close</v-icon>
        </v-card-title>
        <v-card-text id="employee-form" class="form-content">
          <v-row>
            <v-col cols="6">
              <span class="text-uppercase input-label pb-2">{{ $t(`components.employeeForm.default.firstName`) }}</span>

              <v-text-field
                :placeholder="$t(`components.employeeForm.default.givenName`)"
                :class="{ 'employee-field-error': $v.employee.givenName.$error }"
                v-model="employee.givenName"
                class="employee-field mt-5"
                height="44"
                outlined
                required
                clearable
                hide-details
                @input="$v.employee.givenName.$touch()"
                @blur="$v.employee.givenName.$touch()"
              >
                <template v-if="$v.employee.givenName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.employee.givenName"/>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="6">
              <span class="text-uppercase input-label pb-2">{{ $t(`components.employeeForm.default.lastName`) }}</span>

              <v-text-field
                v-model="employee.familyName"
                :class="{ 'employee-field-error': $v.employee.familyName.$error }"
                :placeholder="$t(`components.employeeForm.default.familyName`)"
                class="employee-field mt-5"
                outlined
                clearable
                required
                hide-details
                no-filter
                @input="$v.employee.familyName.$touch()"
                @blur="$v.employee.familyName.$touch()"
              >
                <template v-if="$v.employee.familyName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.employee.familyName"/>
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{ $t(`components.employeeForm.default.email`) }}</span>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="employee.email"
                :placeholder="$t(`components.employeeForm.default.emailPlaceholder`)"
                :class="{ 'employee-field-error': $v.employee.email.$error }"
                class="employee-field email-input"
                outlined
                required
                clearable
                hide-details
                no-filter
                @input="$v.employee.email.$touch()"
                @blur="$v.employee.email.$touch()"
              >
                <template v-if="$v.employee.email.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.employee.email"/>
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="input-label">{{ $t(`components.employeeForm.default.phone`) }}</span>
            </v-col>

            <v-col cols="12">
              <vue-tel-input
                :disabled="mode === 'edit'"
                ref="employee-phone-input"
                filled
                mode="international"
                :class="{ 'employee-field-error': $v.employee.phone.$error }"
                :autoDefaultCountry="false"
                :defaultCountry="countryCode"
                :dropdownOptions="{
                  showDialCodeInList: true,
                  showDialCodeInSelection: true,
                  showFlags: true,
                  width: '240px',
                }"
                :inputOptions="{ showDialCode: false }"
                v-model="employee.phone"
                @country-changed="onCountryChanged"
                @input="$v.employee.phone.$touch()"
                @blur="$v.employee.phone.$touch()"
              >
                <template v-if="$v.employee.phone.$error && !$v.employee.phone.required" v-slot:icon-right>
                  <div class="mr-2 mt-2">
                    <error-tooltip :validator="$v.employee.phone"/>
                  </div>
                </template>
              </vue-tel-input>
            </v-col>

            <v-col cols="12">
              <span class="text-uppercase input-label">{{ $t('components.employeeForm.default.account') }}</span>
            </v-col>

            <v-col cols="12">
              <v-select
                :disabled="mode === 'edit'"
                outlined
                hide-details
                :menu-props="{ bottom: true, offsetY: true }"
                item-text="name"
                v-model="activeCorporation"
                :items="getCorporationsList"
                :loading="corporationsLoading"
                class="employee-field"
                return-object
                :placeholder="$t('components.employeeForm.default.accountPlaceholder')"
              />
            </v-col>

            <v-col cols="12">
              <span class="text-uppercase input-label">{{ $t('components.employeeForm.default.group') }}</span>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="activeGroup"
                outlined
                hide-details
                :menu-props="{ bottom: true, offsetY: true }"
                :loading="groupsLoading"
                :items="businessPassengerGroups"
                item-text="name"
                class="employee-field"
                return-object
                :placeholder="$t('components.employeeForm.default.groupPlaceholder')"
              />
            </v-col>

            <v-col v-if="isRoleCorpoAdmin" cols="12">
              <span class="text-uppercase input-label">{{ $t(`components.employeeForm.default.communicationLanguage`) }}</span>
            </v-col>

            <v-col v-if="isRoleCorpoAdmin" cols="12">
              <v-select
                :items="languages"
                v-model="employee.communicationLanguage"
                class="language-field"
                :menu-props="{ bottom: true, offsetY: true }"
                item-text="name"
                item-value="id"
                outlined
                hide-details
                background-color="#f8f8f8"
              >
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="mt-4">
          <v-btn
            class="text-capitalize"
            color="grey darken-1"
            text
            @click="forceClear()"
          >
            {{ $t(`components.employeeForm.${mode}.cancel`) }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="text-capitalize white--text"
            color="#333333"
            depressed
            :disabled="$v.employee.$invalid || isLoading || formChanged"
            @click="submitDialog"
          >
            {{ $t(`components.employeeForm.${mode}.agree`) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </loader>
</template>

<script>
import { mapGetters } from 'vuex';
import { isEqual } from 'lodash';

import employeeValidation from '@/helpers/rules/employeeValidation';
import { languageComputed, languageMethods } from '@/mixins/language.mixins';
import { businessPassenger } from '@/mixins/passenger.mixins';

import ErrorTooltip from '@/components/elements/ErrorTooltip.vue';

const blankState = {
  businessAccountId: null,
  givenName: '',
  familyName: '',
  phone: '',
  email: '',
  role: 'passenger',
  corporationId: null,
  communicationLanguage: 'en',
};

export default {
  name: 'CreateEmployeeForm',
  mixins: [languageComputed, languageMethods, businessPassenger],
  props: {
    mode: {
      type: String,
      default: 'create',
    },
    employeeData: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    ErrorTooltip,
  },
  validations: {
    ...employeeValidation,
  },
  data() {
    return {
      employee: {},
      activeCorporation: null,
      activeGroup: null,
      corporations: [],
      isLoading: false,
      corporationsLoading: false,
      groupsLoading: false,
      formChanged: false,
    };
  },
  watch: {
    employeeData: {
      handler() {
        if (this.mode === 'edit') {
          this.employee = { ...this.employeeData };
        } else {
          this.employee = { ...blankState };
        }
      },
      immediate: true,
    },
    employee: {
      handler(val) {
        if (this.mode === 'edit') {
          this.formChanged = isEqual(this.getMutableFields(val), this.getMutableFields(this.employeeData));
        }
      },
      deep: true,
    },
    getSelectedCorporationId: {
      async handler(val) {
        if (!val) return;
        if (!this.getCorporationsList.length) return;

        this.activeCorporation = this.getCorporationsList.find((el) => el.id === val);

        if (this.activeCorporation) {
          await this.loadActiveCorporationGroups(this.activeCorporation.id);
        }
      },
      immediate: true,
    },
    selectedLanguage(val) {
      this.employee.communicationLanguage = val;
    },
    activeCorporation: {
      async handler(val) {
        if (!val) return;

        this.groupsLoading = true;
        await this.loadActiveCorporationGroups(val.id);
        this.groupsLoading = false;
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters({
      countryCode: 'bookingChannel/defaultCountry',
    }),
    ...mapGetters('corporations', ['getCorporationsList', 'getSelectedCorporationId']),
    getActionURL() {
      return this.mode === 'edit' ? 'employee/updateEmployee' : 'employee/createEmployee';
    },
    isRoleCorpoAdmin() {
      return this.employee.role === 'corpoAdmin';
    },
    getActionPayload() {
      // const formattedPhone = `+${this.employee.dialCode}${this.employee.phone}`.replaceAll(' ', '');

      if (this.mode === 'edit') {
        return {
          id: this.employee.id,
          givenName: this.employee.givenName,
          familyName: this.employee.familyName,
          email: this.employee.email,
          phone: this.employee.phone.replaceAll(' ', ''),
          role: this.employee.role,
          communicationLanguage: this.employee.communicationLanguage,
          corporationId: this.activeCorporation.id,
        };
      }

      return {
        givenName: this.employee.givenName,
        familyName: this.employee.familyName,
        email: this.employee.email,
        phone: this.employee.phone.replaceAll(' ', ''),
        role: this.employee.role,
        corporationId: this.activeCorporation.id,
        communicationLanguage: this.employee.communicationLanguage,
      };
    },
  },
  methods: {
    forceClear(submitted) {
      this.employee = { ...blankState };

      this.isLoading = false;

      this.$v.$reset();
      this.$emit('closeDialog', submitted);
    },
    getMutableFields(val) {
      return {
        givenName: val.givenName,
        familyName: val.familyName,
        email: val.email,
      };
    },
    onCountryChanged(country) {
      this.employee.dialCode = country.dialCode;
    },
    submitDialog() {
      this.$v.employee.$touch();

      if (this.$v.employee.$invalid) {
        return;
      }

      this.isLoading = true;
      this.$store.dispatch(this.getActionURL, this.getActionPayload)
        .then(() => {
          this.forceClear(true);
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/phoneInput.scss';

  .input-label {
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 12px;
    display: flex;
    align-items: center;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #888888;
  }

  .employee-field-error {
    border: 1px solid #EB5757 !important;
  }

  .employee-field {
    background: #F8F8F8;
    box-sizing: border-box;
    border-radius: 6px;
    font-style: normal;
    line-height: 15px;
    border: 1px solid #EEEEEE;
    letter-spacing: -0.005em;
    color: #333333;
  }
</style>
